import * as React from 'react'
import type { CSSProperties } from 'react'

import { cn } from '@/lib/utils'

/**
 * Badge primitive: pill-shaped status indicator.
 *
 * Semantic variants correspond to the design system's status, ownership, and
 * source categories.
 */

export type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'outline'

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant
}

export function Badge({ className, variant = 'default', style, ...props }: BadgeProps) {
  const combinedStyle: CSSProperties = {
    ...baseStyles.badge,
    ...variantStyles[variant],
    ...style,
  }

  return (
    <span
      style={combinedStyle}
      className={cn('badge', className)}
      {...props}
    />
  )
}

const baseStyles: Record<string, CSSProperties> = {
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '9999px',
    padding: '2px 10px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 1.5,
    transition: 'all var(--transition-fast)',
  },
}

const variantStyles: Record<BadgeVariant, CSSProperties> = {
  default: {
    background: 'var(--bg-tertiary)',
    color: 'var(--text-secondary)',
  },
  primary: {
    background: 'rgba(99, 102, 241, 0.15)',
    color: 'var(--accent-primary)',
  },
  success: {
    background: 'rgba(34, 197, 94, 0.15)',
    color: 'var(--success)',
  },
  warning: {
    background: 'rgba(245, 158, 11, 0.15)',
    color: 'var(--warning)',
  },
  danger: {
    background: 'rgba(239, 68, 68, 0.15)',
    color: 'var(--error)',
  },
  outline: {
    background: 'transparent',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-default)',
  },
}
